<template>
  <div class="w-full flex justify-center pb-16 px-4 sm:px-8 mt-2">
    <div class="w-full max-w-4xl">
      
      <!-- HEADER 1: STANDARD PROGRESS MEASURES -->
      <div class="flex flex-col mb-8 pb-6 border-b border-gray-100 text-left w-full items-start animate-fade-in">
        <h1 class="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight mb-3">
          {{ $t("progressMeasure.pm1") }}
        </h1>
        <p class="text-gray-500 font-medium text-lg">{{ $t("progressMeasure.pm2") }}</p>
      </div>

      <!-- LIST OF STANDARD MEASURES -->
      <div class="flex flex-col space-y-4 mb-12 animate-fade-in-up" v-if="progress_measures && progress_measures.length > 0">
        <div
          v-for="(p, indexp) in progress_measures"
          :key="indexp"
          class="flex items-center p-5 sm:p-6 select-none relative overflow-hidden group comp-card border transition-all duration-300"
          :class="[
            your_progress_measures.includes(p.id) ? 'bg-primary border-primary shadow-lg -translate-y-1' : 'bg-white border-gray-100',
            used_progress_measures.includes(p.id) ? 'opacity-50 grayscale cursor-not-allowed bg-gray-50' : 'cursor-pointer hover:border-gray-300 hover:shadow-md hover:-translate-y-1'
          ]"
          @click="handleMeasureClick(p.id)"
        >
          <!-- Icon Wrapper -->
          <div class="flex-shrink-0 mr-5 sm:mr-6 relative z-10">
            <span class="inline-flex items-center justify-center w-16 h-16 transition-colors duration-300 char-icon-wrapper" 
                  style="border-radius: 1rem;"
                  :class="your_progress_measures.includes(p.id) ? 'bg-white/20' : 'bg-primary/10 group-hover:bg-primary/20'">
              <img :src="require('@/assets/Iconography/Progress measures/PM_' + (parseInt(indexp) + 1) + '.svg')" alt="pm" width="32px" height="32px" 
                   class="transition-all duration-300" 
                   :class="{'brightness-0 invert': your_progress_measures.includes(p.id), 'greyscale-icon': !your_progress_measures.includes(p.id), 'group-hover:scale-110': !used_progress_measures.includes(p.id)}" />
            </span>
          </div>

          <!-- Text Content -->
          <div class="flex-grow text-left relative z-10 w-full overflow-hidden pr-4">
            <h4 class="font-extrabold text-xl mb-1 leading-tight transition-colors duration-300" 
                :class="your_progress_measures.includes(p.id) ? 'text-white' : 'text-gray-800'">
              {{ p.title }}
            </h4>
            <p class="text-sm leading-relaxed font-medium m-0 w-full transition-colors duration-300"
               :class="your_progress_measures.includes(p.id) ? 'text-white' : 'text-gray-500'">
              {{ p.pmMap }}
            </p>
          </div>

          <!-- Checkmark for Selected State -->
          <div class="flex-shrink-0 relative z-10 ml-2 transition-all duration-500" 
               :class="your_progress_measures.includes(p.id) ? 'opacity-100 translate-x-0 text-white' : 'opacity-0 translate-x-4 text-transparent'">
            <div class="p-2 rounded-full" :class="your_progress_measures.includes(p.id) ? 'bg-white/20' : ''">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
            </div>
          </div>
          
          <!-- Lock Icon for Used State -->
          <div v-if="used_progress_measures.includes(p.id)" class="absolute top-4 right-4 text-gray-400">
             <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
          </div>

        </div>
      </div>

      <!-- HEADER 2: CUSTOM PROGRESS MEASURES -->
      <div class="flex flex-col mb-6 mt-6 pb-6 border-b border-gray-100 text-left w-full items-start animate-fade-in" style="animation-delay: 0.1s;">
        <h1 class="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight mb-2">
          {{ $t("progressMeasure.pm3") }}
        </h1>
        <p class="text-gray-500 font-medium text-base">{{ $t("progressMeasure.pm4") }}</p>
      </div>

      <!-- CUSTOM MEASURES LIST -->
      <div class="flex flex-col space-y-4 mb-6 animate-fade-in-up" style="animation-delay: 0.2s;" v-if="custom_progress_measures && custom_progress_measures.length > 0">
        <div
          v-for="(p, indexp) in custom_progress_measures"
          :key="indexp"
          class="flex items-center p-5 sm:p-6 bg-primary border border-primary shadow-md rounded-3xl relative overflow-hidden"
        >
          <div class="flex-shrink-0 mr-5 sm:mr-6 relative z-10">
            <span class="inline-flex items-center justify-center w-16 h-16 bg-white/20 transition-colors duration-300" style="border-radius: 1rem;">
              <img :src="require('@/assets/Iconography/Progress measures/PM_' + (parseInt(indexp) + 1) + '.svg')" alt="pm" width="32px" height="32px" class="brightness-0 invert" />
            </span>
          </div>
          <div class="flex-grow text-left relative z-10">
            <h4 class="font-extrabold text-lg text-white m-0 leading-tight">
              {{ p.cpm }}
            </h4>
          </div>
        </div>
      </div>

      <!-- ADD CUSTOM MEASURE BUTTON -->
      <div class="w-full animate-fade-in-up" style="animation-delay: 0.3s;">
        <div
          @click="add_custom_progress_measure_active=true"
          class="border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-primary/5 hover:border-primary/50 transition-all duration-300 cursor-pointer rounded-3xl p-6 flex flex-col items-center justify-center text-center group"
        >
          <span class="inline-flex items-center justify-center w-14 h-14 bg-white shadow-sm rounded-full mb-3 text-gray-400 group-hover:text-primary transition-colors duration-300">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
          </span>
          <h5 class="font-bold text-gray-600 group-hover:text-primary transition-colors duration-300">
            {{ $t("progressMeasure.pm5") }}
          </h5>
        </div>
      </div>

      <!-- ADD CUSTOM MEASURE PROMPT MODAL -->
      <vs-prompt
        :title="$t('progressMeasure.pm5')"
        :accept-text="$t('progressMeasure.pm6')"
        :cancel-text="$t('progressMeasure.pm9')"
        @cancel="custom_progress_measure=''"
        @accept="addCustomProgressMeasure"
        @close="custom_progress_measure=''"
        :is-valid="custom_progress_measure!==''"
        :active.sync="add_custom_progress_measure_active"
      >
        <div class="con-exemple-prompt p-2">
          <div class="mb-4 font-medium text-gray-700">{{ $t("progressMeasure.pm7") }}</div>
          <vs-input class="w-full" :placeholder="$t('progressMeasure.pm8')" v-model="custom_progress_measure"/>
        </div>
      </vs-prompt>

    </div>
  </div>
</template>

<script>
import uuid4 from 'uuid4';

export default {
  name: "ProgressMeasure",
  data() {
    return {
      progress_measures_list: [],
      your_progress_measures: this.$store.state.addSkill.progressMeasures,
      add_custom_progress_measure_active: false,
      custom_progress_measure: ''
    };
  },
  computed: {
    custom_progress_measures() {
      return this.$store.state.addSkill.customProgressMeasures;
    },
    progress_measures() {
      const selectedComp = this.$store.state.addSkill.competency;
      if (selectedComp && selectedComp.progressMeasures) {
        return selectedComp.progressMeasures;
      }
      return [];
    },
    // Add this new computed property
    used_progress_measures() {
      return this.$store.state.addSkill.usedProgressMeasures || [];
    }
  },
  methods: {
    handleMeasureClick(pmId) {
      if (this.used_progress_measures.includes(pmId)) {
        // Stop them and show the warning
        this.$vs.notify({
          title: 'Skill Already Present',
          text: 'You have already added this measure to your framework.',
          color: 'warning'
        });
        return; 
      }
      
      // If not used, proceed normally
      this.storeProgressMeasure(pmId);
    },
    addCustomProgressMeasure() {
      const customProgressMeasure = {
        cpm: this.custom_progress_measure,
        id: `custom_${ uuid4()}`
      }
      this.$store.commit("addSkill/ADD_CUSTOM_PROGRESS_MEASURE", customProgressMeasure);
      this.custom_progress_measure = null;
    },
    storeProgressMeasure(pm) {
      if(this.your_progress_measures.includes(pm)){
        this.$store
          .dispatch("addSkill/removeProgressMeasure", pm)
          .then(res => {})
          .catch(err => { console.log("could not remove progress measure", err); });
      }
      else{
        this.$store
          .dispatch("addSkill/addProgressMeasure", pm)
          .then(res => {})
          .catch(err => { console.log("could not add progress measure", err); });
      }
    }
  },
  created() {
    const progress_measure = this.$store.state.addSkill.progressMeasures;
    if (progress_measure && progress_measure.length !== 0){
      this.your_progress_measures = progress_measure;
    }
  }
};
</script>

<style scoped>
/* Base Animations */
.animate-fade-in {
  animation: fadeIn 0.4s ease-out forwards;
  opacity: 0;
}
.animate-fade-in-up {
  animation: fadeInDown 0.5s cubic-bezier(0.25, 1, 0.5, 1) forwards;
  opacity: 0;
}

@keyframes fadeIn {
  0% { opacity: 0; }
  100% { opacity: 1; }
}
@keyframes fadeInDown {
  0% { opacity: 0; transform: translateY(15px); }
  100% { opacity: 1; transform: translateY(0); }
}

/* ------------------------------------- 
   LIST CARDS STYLING
   ------------------------------------- */
.comp-card {
  border-radius: 1.5rem !important; /* Forced rounded corners */
}

/* Icon specific styling */
.greyscale-icon { 
  mix-blend-mode: multiply; 
  opacity: 0.85; 
}
.brightness-0 { filter: brightness(0); }
.invert { filter: brightness(0) invert(1); }

/* Remove default Vuesax styling leaks if any */
.items-left{
  text-align: left;
  text-align: -moz-left;
  text-align: -webkit-left;
}
</style>